import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Globe, Shield, ArrowRight, Search, Package, ExternalLink, Download, Car } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const navigate = useNavigate();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState('');
  
  // PWA Install state
  const [canInstall, setCanInstall] = useState(false);
  const deferredPromptRef = useRef(null);

  // Handle PWA install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setCanInstall(false);
      deferredPromptRef.current = null;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPromptRef.current) return;
    
    deferredPromptRef.current.prompt();
    const { outcome } = await deferredPromptRef.current.userChoice;
    
    if (outcome === 'accepted') {
      setCanInstall(false);
    }
    deferredPromptRef.current = null;
  };

  const handleTrackPackage = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;
    
    setTrackingLoading(true);
    setTrackingError('');
    setTrackingResult(null);
    
    try {
      const response = await axios.get(`${API}/track/${trackingNumber.trim()}`);
      setTrackingResult(response.data);
    } catch (error) {
      setTrackingError('Unable to track package. Please check the tracking number and try again.');
    } finally {
      setTrackingLoading(false);
    }
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      pending: { label: 'Pending', color: 'bg-slate-500', description: 'Package registered, awaiting arrival at warehouse' },
      received_by_dac: { label: 'Received by DAC', color: 'bg-blue-500', description: '✅ Package received at our US warehouse and ready for shipment' },
      in_transit: { label: 'In Transit', color: 'bg-orange-500', description: 'Package is on its way to Guyana' },
      delivered: { label: 'Delivered', color: 'bg-emerald-500', description: 'Package has been delivered in Guyana' }
    };
    return statusMap[status] || { label: status, color: 'bg-slate-500', description: '' };
  };

  useEffect(() => {
    // Scroll animation observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    // Observe all elements with animate-on-scroll class
    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="landing-hero flex items-center justify-center text-white px-4">
        <div className="max-w-6xl w-full">
          <div className="text-center">
            <div className="inline-block bg-white/10 backdrop-blur-sm p-4 rounded-2xl mb-6">
              <img 
                src="/dac-logo.png" 
                alt="DAC SHIPPING" 
                className="h-20 sm:h-24 mx-auto"
                style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' }}
              />
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 tracking-tight">
              USA to Guyana<br />Shipping Made Easy
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 mb-8 max-w-2xl mx-auto">
              Get your own US shipping address. We receive, consolidate, and forward your packages to Guyana.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="get-started-btn"
                size="lg"
                onClick={() => navigate('/signup')}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg font-semibold"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    data-testid="track-package-btn"
                    size="lg"
                    className="bg-white text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg font-semibold border-2 border-orange-500"
                  >
                    <Package className="mr-2 h-5 w-5" />
                    Track Package
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-2xl">
                      <Package className="h-6 w-6 text-orange-500" />
                      Track Your Package
                    </DialogTitle>
                    <DialogDescription>
                      Enter your tracking number to check your package status
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleTrackPackage} className="space-y-4">
                    <Input
                      data-testid="tracking-input"
                      placeholder="Enter tracking number (e.g., 1Z999AA10123456784)"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      className="h-12 text-lg"
                    />
                    <Button
                      data-testid="track-btn"
                      type="submit"
                      disabled={trackingLoading}
                      className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
                    >
                      {trackingLoading ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin">⏳</span> Tracking...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Search className="h-5 w-5" /> Track Package
                        </span>
                      )}
                    </Button>
                  </form>

                  {/* Tracking Error */}
                  {trackingError && (
                    <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-lg text-sm">
                      {trackingError}
                    </div>
                  )}

                  {/* Tracking Result */}
                  {trackingResult && (
                    <div className="border rounded-xl p-4 mt-2">
                      {trackingResult.found ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between border-b pb-3">
                            <div>
                              <p className="text-xs text-slate-500">Tracking Number</p>
                              <p className="font-mono font-bold text-slate-900">{trackingResult.tracking_number}</p>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-white text-sm font-semibold ${getStatusDisplay(trackingResult.status).color}`}>
                              {getStatusDisplay(trackingResult.status).label}
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-slate-500">Carrier</p>
                              <p className="font-semibold">{trackingResult.carrier}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Weight</p>
                              <p className="font-semibold">{trackingResult.weight}</p>
                            </div>
                          </div>

                          <div className="bg-emerald-50 p-3 rounded-lg">
                            <p className="text-emerald-800 text-sm font-semibold flex items-center gap-2">
                              ✅ {getStatusDisplay(trackingResult.status).description}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-slate-600 text-sm">{trackingResult.message}</p>
                          
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-slate-700">Check external trackers:</p>
                            <div className="flex flex-wrap gap-2">
                              <a
                                href={trackingResult.external_links?.parcelsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm transition-colors"
                              >
                                Parcels App <ExternalLink className="h-3 w-3" />
                              </a>
                              <a
                                href={trackingResult.external_links?.['17track']}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm transition-colors"
                              >
                                17TRACK <ExternalLink className="h-3 w-3" />
                              </a>
                              <a
                                href={trackingResult.external_links?.aftership}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm transition-colors"
                              >
                                AfterShip <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                          </div>

                          <p className="text-amber-700 text-xs bg-amber-50 p-2 rounded">
                            <strong>Note:</strong> Packages may take 1-2 days to appear in our system after delivery.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              <Button
                data-testid="login-btn"
                size="lg"
                variant="outline"
                onClick={() => navigate('/login')}
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg font-semibold"
              >
                Login
              </Button>
            </div>
            {/* Download App Button - Shows only when PWA is installable */}
            {canInstall && (
              <div className="mt-6 animate-fade-in">
                <Button
                  data-testid="download-app-btn"
                  size="lg"
                  onClick={handleInstallClick}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Download App
                </Button>
                <p className="text-white/70 text-sm mt-2">Install for faster access & notifications</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-slate-900 animate-on-scroll">Why Choose DAC SHIPPING?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 animate-on-scroll delay-100">
              <div className="h-16 mb-4 flex items-center">
                <img src="/dac-logo.png" alt="DAC Shipping" className="h-full w-auto object-contain" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-slate-900">Your US Address</h3>
              <p className="text-slate-600">Get a real US address instantly. Use it for all your online shopping needs.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 animate-on-scroll delay-200">
              <Globe className="h-12 w-12 text-orange-500 mb-4" />
              <h3 className="text-2xl font-bold mb-3 text-slate-900">Fast to Guyana</h3>
              <p className="text-slate-600">We forward your packages directly to Guyana with full tracking and insurance.</p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 animate-on-scroll delay-300">
              <Shield className="h-12 w-12 text-orange-500 mb-4" />
              <h3 className="text-2xl font-bold mb-3 text-slate-900">Secure & Reliable</h3>
              <p className="text-slate-600">Your packages are safe with us. Full insurance and real-time tracking.</p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-slate-900 animate-on-scroll">How It Works</h2>
          <p className="text-center text-slate-600 mb-12 animate-on-scroll">Get your packages from USA to Guyana in 6 simple steps</p>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-6">
            <div className="text-center animate-on-scroll delay-100">
              <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="font-bold text-lg mb-2">Sign Up</h3>
              <p className="text-sm text-slate-600">Create your free account</p>
            </div>
            <div className="text-center animate-on-scroll delay-200">
              <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="font-bold text-lg mb-2">Get US Address</h3>
              <p className="text-sm text-slate-600">Receive your unique US shipping address</p>
            </div>
            <div className="text-center animate-on-scroll delay-300">
              <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="font-bold text-lg mb-2">Shop Online</h3>
              <p className="text-sm text-slate-600">Use your US address at checkout</p>
            </div>
            <div className="text-center animate-on-scroll delay-400">
              <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">4</div>
              <h3 className="font-bold text-lg mb-2">We Receive & Scan</h3>
              <p className="text-sm text-slate-600">Warehouse receives and scans your package</p>
            </div>
            <div className="text-center animate-on-scroll delay-500">
              <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">5</div>
              <h3 className="font-bold text-lg mb-2">Ship to Guyana</h3>
              <p className="text-sm text-slate-600">We ship your package to Guyana</p>
            </div>
            <div className="text-center animate-on-scroll delay-600">
              <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">6</div>
              <h3 className="font-bold text-lg mb-2">Pickup/Delivery</h3>
              <p className="text-sm text-slate-600">Collect your package in Guyana</p>
            </div>
          </div>

          {/* Pricing & Info */}
          <div className="mt-16 grid md:grid-cols-2 gap-8">
            <div className="bg-slate-50 p-8 rounded-lg border-2 border-slate-200 animate-on-scroll">
              <h3 className="text-2xl font-bold mb-4 text-slate-900">Shipping Rates</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Air Freight:</span>
                  <span className="text-2xl font-bold text-orange-600">$667/lb</span>
                </div>
                <p className="text-sm text-slate-600 mt-4">
                  <strong>Handling Fee:</strong> Covers warehouse processing, scanning, and consolidation services.
                </p>
              </div>
            </div>

            <div className="bg-orange-50 p-8 rounded-lg border-2 border-orange-200 animate-on-scroll delay-100">
              <h3 className="text-2xl font-bold mb-4 text-slate-900">Important Note</h3>
              <p className="text-slate-700 leading-relaxed">
                <strong>Customs Duties:</strong> All customs duties and taxes are determined by Guyana Customs authorities. 
                These charges are separate from our shipping fees and must be paid upon arrival in Guyana.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Shopping Service Section */}
      <div className="py-20 px-4 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-on-scroll">
            <div className="inline-block bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              SPECIAL SERVICE
            </div>
            <h2 className="text-4xl font-bold mb-6">Don't Have a Credit Card?</h2>
            <p className="text-xl text-slate-300 mb-8">We'll Shop For You!</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/20 animate-on-scroll delay-100">
            <p className="text-lg text-slate-200 mb-8 leading-relaxed">
              No credit card? No problem! We'll purchase items online on your behalf and ship them directly to Guyana. 
              Simply send us the product link and we'll handle everything.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-orange-500/20 backdrop-blur rounded-xl p-6 border border-orange-500/30">
                <div className="text-5xl font-bold text-orange-400 mb-2">7%</div>
                <p className="text-sm text-slate-300">Service Fee</p>
                <p className="text-xs text-slate-400 mt-2">Only 7% of the item's cost price</p>
              </div>
              
              <div className="bg-orange-500/20 backdrop-blur rounded-xl p-6 border border-orange-500/30">
                <div className="text-5xl font-bold text-orange-400 mb-2">40%</div>
                <p className="text-sm text-slate-300">Down Payment</p>
                <p className="text-xs text-slate-400 mt-2">Pay only 40% upfront to get started</p>
              </div>
            </div>
            
            <div className="bg-slate-900/50 rounded-xl p-6 mb-8">
              <h4 className="font-bold text-lg mb-3 text-orange-400">How It Works:</h4>
              <ol className="text-left space-y-2 text-slate-300">
                <li className="flex items-start gap-3">
                  <span className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">1</span>
                  <span>Send us the product link via WhatsApp</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">2</span>
                  <span>We'll provide you with a quote (item cost + 7% service fee + shipping)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">3</span>
                  <span>Pay 40% down payment to confirm your order</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">4</span>
                  <span>We purchase and ship your item to Guyana</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">5</span>
                  <span>Pay remaining balance upon delivery</span>
                </li>
              </ol>
            </div>
            
            <a 
              href="https://wa.me/15926350592?text=Hello%20I%20would%20like%20a%20quote%20for%20the%20shopping%20service"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-full text-lg font-bold transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Request a Quote on WhatsApp
            </a>
            
            <p className="text-sm text-slate-400 mt-4">
              Fast response • Secure payment • Trusted service
            </p>
          </div>
        </div>
      </div>

      {/* Vehicle Pre-Ordering Section */}
      <div className="py-20 px-4 bg-gradient-to-br from-blue-950 via-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-on-scroll">
            <div className="inline-block bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              NEW SERVICE
            </div>
            <h2 className="text-4xl font-bold mb-6">Vehicle Pre-Ordering</h2>
            <p className="text-xl text-slate-300 mb-8">Reserve Your Next Ride from Japan — We Handle the Rest</p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/20 animate-on-scroll delay-100">
            <div className="inline-flex items-center justify-center bg-blue-500/20 border border-blue-400/40 w-20 h-20 rounded-2xl mb-6">
              <Car className="h-10 w-10 text-blue-300" />
            </div>

            <p className="text-lg text-slate-200 mb-8 leading-relaxed">
              Found a car, SUV, van or truck you love on <span className="font-semibold text-blue-200">SBT Japan</span> or <span className="font-semibold text-blue-200">Be Forward</span>? Lock it in before someone else does. We source, inspect, purchase and ship it straight to Guyana — so you skip the hassle and get the ride you actually want.
            </p>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-blue-500/15 backdrop-blur rounded-xl p-5 border border-blue-400/30">
                <div className="text-2xl font-bold text-blue-300 mb-1">SBT & Be Forward</div>
                <p className="text-sm text-slate-300">Primary Japanese exporters — plus other trusted dealers on request.</p>
              </div>
              <div className="bg-blue-500/15 backdrop-blur rounded-xl p-5 border border-blue-400/30">
                <div className="text-2xl font-bold text-blue-300 mb-1">All Paperwork</div>
                <p className="text-sm text-slate-300">Bill of Sale, export certificate and Guyana customs clearance handled end-to-end.</p>
              </div>
              <div className="bg-blue-500/15 backdrop-blur rounded-xl p-5 border border-blue-400/30">
                <div className="text-2xl font-bold text-blue-300 mb-1">RORO or Container</div>
                <p className="text-sm text-slate-300">Choose the shipping mode from Japan that fits your budget and timeline.</p>
              </div>
            </div>

            <div className="bg-slate-900/50 rounded-xl p-6 mb-8">
              <h4 className="font-bold text-lg mb-3 text-blue-300">How It Works:</h4>
              <ol className="text-left space-y-2 text-slate-300">
                <li className="flex items-start gap-3">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">1</span>
                  <span>Send us the SBT Japan or Be Forward listing (or the make/model you want)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">2</span>
                  <span>We give you a landed quote (vehicle + Japan fees + shipping + duties)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">3</span>
                  <span>Pay a pre-order deposit to lock the vehicle with the exporter</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">4</span>
                  <span>We purchase, inspect, and ship it from Japan to Georgetown</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">5</span>
                  <span>Settle the balance and drive home</span>
                </li>
              </ol>
            </div>

            <a
              data-testid="vehicle-preorder-whatsapp"
              href="https://wa.me/15926350592?text=Hello%20I%20would%20like%20to%20pre-order%20a%20vehicle%20from%20Japan%20(SBT%20or%20Be%20Forward)"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-full text-lg font-bold transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Start Your Pre-Order
            </a>

            <p className="text-sm text-slate-400 mt-4">
              SBT Japan & Be Forward • Licensed importer • Door-to-port or door-to-door
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 px-4 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Start Shipping?</h2>
          <p className="text-lg text-slate-300 mb-8">Join hundreds of happy customers shipping from USA to Guyana</p>
          <Button
            data-testid="cta-signup-btn"
            size="lg"
            onClick={() => navigate('/signup')}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg font-semibold"
          >
            Create Free Account
          </Button>
        </div>
      </div>

      {/* Contact Section */}
      <div className="py-12 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl font-bold mb-6 text-slate-900 text-center">Contact & Support</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <a 
              href="https://wa.me/15926350592" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-3 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-6 rounded-lg transition-all font-semibold text-center"
            >
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <div>
                <div className="font-bold">WhatsApp</div>
                <div className="text-sm">+1 592 635 0592</div>
              </div>
            </a>
            <a 
              href="mailto:support@dacshipping.com" 
              className="flex flex-col items-center gap-3 bg-slate-700 hover:bg-slate-800 text-white px-6 py-6 rounded-lg transition-all font-semibold text-center"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <div>
                <div className="font-bold">Email Support</div>
                <div className="text-sm">support@dacshipping.com</div>
              </div>
            </a>
            <div className="flex flex-col items-center gap-3 bg-orange-500 text-white px-6 py-6 rounded-lg font-semibold text-center">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div>
                <div className="font-bold">Our Location</div>
                <div className="text-sm">22 Shell Road, Kitty</div>
                <div className="text-sm">Georgetown, Guyana</div>
              </div>
            </div>
          </div>
          <div className="mt-6 text-center">
            <a 
              href="https://instagram.com/DAC_Shipping" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-slate-700 hover:text-orange-500 transition-colors"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <span className="font-semibold">Follow us @DAC_Shipping</span>
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <img src="/dac-logo.png" alt="DAC SHIPPING" className="h-12" />
              <div>
                <p className="font-bold text-lg">DAC SHIPPING</p>
                <p className="text-sm text-slate-400">USA to Guyana Shipping</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="font-semibold">Business Address</p>
              <p className="text-slate-400">22 Shell Road, Kitty</p>
              <p className="text-slate-400">Georgetown, Guyana</p>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-slate-700 text-center text-slate-500 text-sm">
            <p>© {new Date().getFullYear()} DAC SHIPPING. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;